import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PersonPinCircleOutlinedIcon from "@mui/icons-material/PersonPinCircleOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { CashkaroService } from "../../../core/services";
import { CC, categoryColor, PayoutChip, CampaignMetaRow, CampaignTermsHeader, termsListSx } from "./CashkaroLayout";
import { AddCampaignProductDialog } from "./AddCampaignProductDialog";

const CURRENCY_SYMBOLS: Record<string, string> = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ" };
const sym = (c: string | null | undefined) => (c && CURRENCY_SYMBOLS[c]) || c || "";

// tracking_window_minutes is always in minutes (see campaignNormalizer.ts) —
// format back to whichever unit reads cleanest.
function formatDuration(minutes: number | null | undefined): string | null {
  if (!minutes && minutes !== 0) return null;
  if (minutes % 1440 === 0) return `${minutes / 1440} day${minutes / 1440 === 1 ? "" : "s"}`;
  if (minutes % 60 === 0) return `${minutes / 60} hour${minutes / 60 === 1 ? "" : "s"}`;
  return `${minutes} min`;
}

// Colors the connector between two steps once the step it follows is done,
// instead of leaving every segment the same neutral gray regardless of
// progress — makes the stepper actually read as "how far along am I."
const ProgressConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.root}`]: { top: 12 },
  [`& .${stepConnectorClasses.line}`]: { borderColor: CC.border, borderTopWidth: 2 },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: { borderColor: CC.primary },
}));

export const CashkaroCampaignDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.UserReducer?.UserData);
  const influencerId = user?.influencer_id;
  const accountId = user?.account_id;
  const userId = user?.user_id;

  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState<any>(null);
  const [cached, setCached] = useState<any>(null);
  const [normalized, setNormalized] = useState<any>(null);
  const [accessStatus, setAccessStatus] = useState<string>("none");
  const [campaignUnavailable, setCampaignUnavailable] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  // Fires the silent access request at most once per mount — landing on this page is
  // most often already-approved (the shared Trackier account is usually already cleared
  // for a campaign shown at all), so this saves the extra click in the common case; the
  // "Get access" button below still covers the retry/pending case.
  const autoRequestedRef = useRef(false);

  const [targetUrl, setTargetUrl] = useState("");
  const [label, setLabel] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatedLinkId, setGeneratedLinkId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Post-generation "promote it" actions — cross-post the freshly generated link
  // straight onto the influencer's own Storefront / Link in Bio (same endpoints
  // My Links uses), so the natural next step is right here instead of a separate trip.
  const [addingStorefront, setAddingStorefront] = useState(false);
  const [addedStorefront, setAddedStorefront] = useState(false);
  const [addingBio, setAddingBio] = useState(false);
  const [addedBio, setAddedBio] = useState(false);
  const [crossPostSnackbar, setCrossPostSnackbar] = useState<{ message: string; severity: "success" | "info" | "error"; actionTo?: string } | null>(null);

  // If the brand has linked a Storefront to this campaign AND explicitly featured
  // products in it (StorefrontCashkaroLink + the per-product toggle, brand side),
  // `data.storefront` comes back with only those products — never the brand's
  // whole catalog, and never carried over from a previously-linked campaign.
  const [linkedStorefront, setLinkedStorefront] = useState<{ storefront_id: number; slug: string; store_name: string; website_url: string | null } | null>(null);
  // Combined for rendering only — brand-curated (data.storefront.products) and this
  // influencer's own finds (data.my_products, always private to them, independent
  // of any storefront link) are two separate id spaces, each item tagged __source
  // so generate/select know which backend param to use.
  const [storefrontProducts, setStorefrontProducts] = useState<any[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedProductSource, setSelectedProductSource] = useState<"storefront" | "mine" | null>(null);
  // Two clear choices instead of a form with an optional picker buried in it —
  // only relevant once there's actually something to pick from.
  const [promoMode, setPromoMode] = useState<"product" | "site">("site");

  // Destination URL before any product is picked (landing page / campaign preview) —
  // remembered so deselecting a product has something sensible to revert to.
  const [defaultTargetUrl, setDefaultTargetUrl] = useState("");

  // "Add a product I found" — silently opens the brand's real site through a
  // tracked whole-site link first (so any purchase during that visit is still
  // attributed, even if the influencer buys something other than what they
  // came to add), then lets them paste the specific product's URL. The tracked
  // link itself is never shown — it needs to feel like an ordinary click.
  const [openingSite, setOpeningSite] = useState(false);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);

  useEffect(() => {
    if (!id || !influencerId) return;
    setLoading(true);
    CashkaroService.getCampaignDetail(id, influencerId)
      .then((res) => {
        const data = res?.data || {};
        setCampaign(data.campaign);
        setCached(data.cached);
        setNormalized(data.normalized || null);
        setAccessStatus(data.access_status || "none");
        setCampaignUnavailable(Boolean(data.campaign_unavailable));
        if (!targetUrl) {
          const initialUrl =
            data.normalized?.landing_pages?.[0]?.tracking_link ||
            data.campaign?.preview_url ||
            data.cached?.preview_url ||
            "";
          setTargetUrl(initialUrl);
          setDefaultTargetUrl(initialUrl);
        }
        if (data.storefront?.slug) setLinkedStorefront(data.storefront);

        const brandProducts = (data.storefront?.products || []).map((p: any) => ({ ...p, __source: "storefront" as const }));
        const myOwnProducts = (data.my_products || []).map((p: any) => ({ ...p, __source: "mine" as const }));
        const allProducts = [...brandProducts, ...myOwnProducts];
        setStorefrontProducts(allProducts);
        // Default to the product picker when there's actually something to pick —
        // otherwise this campaign behaves exactly like one with nothing added yet.
        // Clear the whole-site URL set above so Generate Link can't silently fire
        // with it while the picker (not the URL field) is what's actually shown.
        if (allProducts.length > 0) {
          setPromoMode("product");
          setTargetUrl("");
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, influencerId]);

  const hasPickableProducts = storefrontProducts.length > 0;

  // Clicking the already-selected product deselects it (toggle), reverting the
  // destination URL to whatever it was before any product was picked. Two separate
  // id spaces (brand-curated vs this influencer's own finds) share one grid, so
  // selection is keyed on (id, source) together, not id alone.
  const selectProduct = (p: any) => {
    if (selectedProductId === p.product_id && selectedProductSource === p.__source) {
      setSelectedProductId(null);
      setSelectedProductSource(null);
      // Still in "Promote a Product" mode (that's the only place this is called
      // from) — clear rather than silently falling back to the whole-site URL,
      // which isn't shown anywhere in this mode.
      setTargetUrl("");
      return;
    }
    setSelectedProductId(p.product_id);
    setSelectedProductSource(p.__source);
    setTargetUrl(p.product_url);
  };

  const title = normalized?.title || campaign?.title || cached?.title;

  // Landing here directly (a "pick a product to promote" link, a bookmark, the
  // preview dialog's "open full page") used to just tell an unapproved influencer to
  // go back to the campaigns list — same silent request+live-reprobe CashkaroCampaigns.tsx
  // does on "Get My Link", just triggerable right here instead of forcing a detour.
  const handleRequestAccess = async () => {
    if (!id || !influencerId) return;
    setRequestingAccess(true);
    setError(null);
    try {
      const res = await CashkaroService.requestCampaignAccess(id, influencerId);
      setAccessStatus(res?.data?.access_status || "pending");
    } catch {
      setError("Could not check campaign access right now — please try again.");
    } finally {
      setRequestingAccess(false);
    }
  };

  useEffect(() => {
    if (loading || campaignUnavailable || accessStatus !== "none" || autoRequestedRef.current) return;
    autoRequestedRef.current = true;
    handleRequestAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, campaignUnavailable, accessStatus]);

  const handleGenerateLink = async () => {
    if (!targetUrl.trim()) return;
    setGenerating(true);
    setError(null);
    setGeneratedLink(null);
    setGeneratedLinkId(null);
    setAddedStorefront(false);
    setAddedBio(false);
    try {
      const res = await CashkaroService.generateLink({
        influencer_id: influencerId,
        campaign_id: Number(id),
        target_url: targetUrl.trim(),
        campaign_title: title || undefined,
        label: label.trim() || undefined,
        storefront_product_id: selectedProductSource === "storefront" ? selectedProductId || undefined : undefined,
        campaign_product_id: selectedProductSource === "mine" ? selectedProductId || undefined : undefined,
        account_id: accountId || undefined,
        user_id: userId || undefined,
      });
      if (res?.data?.campaign_unavailable) {
        setCampaignUnavailable(true);
        setError(res.data.message || "This campaign is no longer available.");
      } else if (res?.data?.requires_access) {
        setError(res.data.message || "Request access to this campaign first.");
      } else if (res?.data?.link?.tracking_url) {
        // Redirect on our own domain, never the raw Trackier tracking_url — it
        // embeds our shared pub_id and internal cctm/product params in plaintext.
        const link = res.data.link;
        setGeneratedLink(link.click_token ? CashkaroService.clickUrl(link.click_token) : link.tracking_url);
        setGeneratedLinkId(link.id ?? null);
      } else {
        setError("Could not generate a link right now — please try again.");
      }
    } catch (e) {
      setError("Could not generate a link right now — please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!generatedLink) return;
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Same endpoints/pattern as the My Links table — cross-post the just-generated
  // link straight onto the influencer's Storefront/Bio without a separate trip.
  const handleAddToStorefront = async () => {
    if (!generatedLinkId || !influencerId || !accountId) return;
    setAddingStorefront(true);
    try {
      const res = await CashkaroService.addLinkToStorefront({ link_id: generatedLinkId, influencer_id: influencerId, account_id: accountId });
      if (res?.data?.requires_setup) {
        setCrossPostSnackbar({ message: res.data.message || "Set up your Zap Storefront first", severity: "info", actionTo: "/influencer/storefront" });
      } else {
        setAddedStorefront(true);
        setCrossPostSnackbar({ message: res?.data?.already_added ? "Already on your Storefront" : "Added to your Storefront", severity: "success" });
      }
    } catch {
      setCrossPostSnackbar({ message: "Could not add to Storefront right now — please try again.", severity: "error" });
    } finally {
      setAddingStorefront(false);
    }
  };

  const handleAddToBio = async () => {
    if (!generatedLinkId || !influencerId || !accountId) return;
    setAddingBio(true);
    try {
      const res = await CashkaroService.addLinkToBio({ link_id: generatedLinkId, influencer_id: influencerId, account_id: accountId });
      if (res?.data?.requires_setup) {
        setCrossPostSnackbar({ message: res.data.message || "Set up your Zap Link in Bio page first", severity: "info", actionTo: "/influencer/link-in-bio" });
      } else {
        setAddedBio(true);
        setCrossPostSnackbar({ message: res?.data?.already_added ? "Already on your Bio page" : "Added to your Bio page", severity: "success" });
      }
    } catch {
      setCrossPostSnackbar({ message: "Could not add to Bio right now — please try again.", severity: "error" });
    } finally {
      setAddingBio(false);
    }
  };

  // "Add a product I found" step 1: silently generate the campaign's whole-site
  // tracked link and open the brand's real site through it — never shown in the
  // UI, so it reads as an ordinary click, not a special tracking link. Best-effort:
  // if generation fails here, the influencer can still open the dialog and paste
  // a URL manually, they just won't have browsed through a tracked visit first.
  const handleOpenSiteAndAddProduct = async () => {
    if (!influencerId) return;
    // Prefer the linked brand storefront's real website when there is one; otherwise
    // fall back to the campaign's own default URL — this button is independent of
    // whether a storefront is linked at all, it just has a better destination when one is.
    const siteUrl = linkedStorefront?.website_url || defaultTargetUrl;
    if (siteUrl) {
      setOpeningSite(true);
      try {
        const res = await CashkaroService.generateLink({
          influencer_id: influencerId,
          campaign_id: Number(id),
          target_url: siteUrl,
          campaign_title: title || undefined,
          account_id: accountId || undefined,
          user_id: userId || undefined,
          // This is just infrastructure to open a tracked tab, not a link the
          // influencer asked for — keep it out of My Links.
          skip_history: true,
        });
        if (res?.data?.link?.tracking_url) {
          window.open(res.data.link.tracking_url, "_blank", "noopener");
        }
      } catch {
        // Best-effort, see comment above.
      } finally {
        setOpeningSite(false);
      }
    }
    setAddProductDialogOpen(true);
  };

  const handleProductAdded = (product: any) => {
    if (product) setStorefrontProducts((prev) => [{ ...product, __source: "mine" as const }, ...prev]);
    setPromoMode("product");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={26} sx={{ color: CC.primary }} />
      </Box>
    );
  }

  // Sanitized server-side by normalizeCampaign — safe to render with
  // dangerouslySetInnerHTML. Never fall back to campaign.description, which
  // is Trackier's raw, unsanitized HTML.
  const description = normalized?.description;
  const termsHtml = normalized?.terms_html;
  const thumbnail = normalized?.thumbnail || cached?.thumbnail;
  const rawCategories = normalized?.categories?.length ? normalized.categories : cached?.categories;
  const categories: string[] = Array.isArray(rawCategories) ? rawCategories : [];
  const trackingWindow = formatDuration(normalized?.tracking_window_minutes);
  const validationDays = normalized?.validation_window_days;
  const landingPages: Array<{ id: number | null; title: string | null; tracking_link: string | null }> =
    normalized?.landing_pages || [];

  const fieldSx = {
    fontFamily: CC.font,
    "& .MuiOutlinedInput-root": {
      borderRadius: CC.radiusSm,
      fontFamily: CC.font,
      "& fieldset": { borderColor: CC.border },
      "&:hover fieldset": { borderColor: CC.borderHover },
      "&.Mui-focused fieldset": { borderColor: CC.primary },
    },
    "& .MuiInputLabel-root": { fontFamily: CC.font },
  };

  // A visible sense of progress through the three things an influencer actually
  // needs to do here — request access, generate a link, then promote it.
  const step1Done = accessStatus === "approved";
  const step2Done = !!generatedLink;
  const step3Done = addedStorefront || addedBio;
  const activeStep = !step1Done ? 0 : !step2Done ? 1 : !step3Done ? 2 : 3;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
        onClick={() => navigate("/influencer/brand-deals")}
        sx={{ mb: 2, color: CC.textSecondary, fontFamily: CC.font, textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: CC.bg } }}
      >
        Back to campaigns
      </Button>

      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexDirection: { xs: "column", lg: "row" } }}>
      <Box sx={{ width: { xs: "100%", lg:600 }, flexShrink: 0, position: { lg: "sticky" }, top: { md: 88 } }}>
      <Paper
        elevation={0}
        sx={{ borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, overflow: "hidden", boxShadow: "0 1px 3px rgba(15,23,42,.06)" }}
      >
        <Box sx={{ p: 3, display: "flex", gap: 2.5, alignItems: "flex-start", flexWrap: "wrap" }}>
          <>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: CC.radiusMd,
                // White ground, not the page's gray surface — most brand logos
                // already assume a white background baked into the source
                // image, so a gray box shows through as a visible seam/box
                // around the logo.
                bgcolor: "#fff",
                border: `1px solid ${CC.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {thumbnail ? (
                <>
                <Box sx={{ width: "100%", height: "100%", p: 1.25, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Box component="img" src={thumbnail} alt={title} sx={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }} />
                </Box>
                
                </>
              ) : (
                <StorefrontOutlinedIcon sx={{ fontSize: 32, color: "#c7cad1" }} />
              )}
            </Box>
            
            <Box sx={{ display: "flex", flexDirection:'column', gap: 0, flexWrap: "wrap", my: 0 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
                {title}
              </Typography>
              <Box sx={{ display: "flex", gap: 0, flexWrap: "wrap", my: 0.35 }}>
                {categories.map((cat) => {
                  const color = categoryColor(cat);
                  return (
                    <Chip
                      key={cat}
                      label={cat}
                      size="small"
                      sx={{ textTransform: "capitalize", height: 22, fontSize: 11, fontFamily: CC.font, borderRadius: "999px", bgcolor: `${color}1a`, color }}
                    />
                  );
                })}
                {/* Payout figures must never render for a campaign flagged hide_payout — see normalizeCampaign rule 6. */}
                {!normalized?.hide_payout && normalized?.payout_summary && <PayoutChip label={normalized.payout_summary} />}
              </Box>
              <CampaignMetaRow trackingWindow={trackingWindow} validationDays={validationDays} sx={{ mb: 1 }} />
            </Box>
          </>
          <Box sx={{ flexGrow: 1, minWidth: 200 }}>
            
            {description && (
              <Typography
                variant="body2"
                sx={{ color: CC.textSecondary, fontFamily: CC.font, lineHeight: 1.6, maxHeight: 160, overflowY: "auto" }}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </Box>
        </Box>
        {termsHtml && (
          <Accordion disableGutters elevation={0} square sx={{ borderTop: `1px solid ${CC.border}`, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: CC.textSecondary }} />} sx={{ bgcolor: CC.bg }}>
              <CampaignTermsHeader />
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ color: CC.textSecondary, fontFamily: CC.font, lineHeight: 1.6, ...termsListSx }} dangerouslySetInnerHTML={{ __html: termsHtml }} />
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: { lg: 640 }, display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>

      {!campaignUnavailable && (
        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, boxShadow: "0 1px 3px rgba(15,23,42,.06)" }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<ProgressConnector />}
            sx={{
              "& .MuiStepIcon-root": { fontSize: 26 },
              "& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed": { color: CC.primary },
              "& .MuiStepLabel-label": { fontFamily: CC.font },
              "& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed": { color: CC.textPrimary, fontWeight: 600 },
            }}
          >
            <Step completed={step1Done}>
              <StepLabel>Get access</StepLabel>
            </Step>
            <Step completed={step2Done}>
              <StepLabel>Generate your link</StepLabel>
            </Step>
            <Step completed={step3Done}>
              <StepLabel>Promote it</StepLabel>
            </Step>
          </Stepper>
        </Paper>
      )}

      {campaignUnavailable ? (
        <Alert severity="warning" sx={{ borderRadius: CC.radiusMd, fontFamily: CC.font }}>
          This campaign is no longer available — the brand may have paused or ended it. Any links you already
          generated for it will stop tracking new clicks.
        </Alert>
      ) : accessStatus !== "approved" ? (
        <Alert
          severity={accessStatus === "pending" ? "warning" : "info"}
          sx={{ borderRadius: CC.radiusMd, fontFamily: CC.font }}
          action={
            <Button
              size="small"
              onClick={handleRequestAccess}
              disabled={requestingAccess}
              sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
            >
              {requestingAccess ? "Checking…" : accessStatus === "pending" ? "Check again" : "Get access"}
            </Button>
          }
        >
          {accessStatus === "pending"
            ? "Your access request for this campaign is pending approval."
            : "You need access to this campaign before generating a link."}
        </Alert>
      ) : (
        <Paper elevation={0} sx={{ p: 3, borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, boxShadow: "0 1px 3px rgba(15,23,42,.06)" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LinkOutlinedIcon fontSize="small" sx={{ color: CC.primary }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
                Generate a trackable link
              </Typography>
            </Box>
            <Button
              size="medium"
              onClick={handleOpenSiteAndAddProduct}
              disabled={openingSite}
              startIcon={openingSite ? <CircularProgress size={14} /> : undefined}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                fontFamily: CC.font,
                border: `1px solid ${CC.border}`,
                borderRadius: "999px",
                color: CC.textPrimary,
                paddingX: 2,
                "&:hover": { borderColor: CC.borderHover, bgcolor: CC.bg },
              }}
            >
              {openingSite ? "Opening site…" : "Add a product I found"}
            </Button>
          </Box>
          {hasPickableProducts && (
            <ToggleButtonGroup
              value={promoMode}
              exclusive
              size="small"
              onChange={(_, v) => {
                if (!v) return;
                setPromoMode(v);
                if (v === "site") {
                  setSelectedProductId(null);
                  setSelectedProductSource(null);
                  setTargetUrl(defaultTargetUrl);
                } else if (!selectedProductId) {
                  // Switching to the product picker with nothing picked yet — clear
                  // the whole-site URL so Generate Link can't silently fire with it.
                  setTargetUrl("");
                }
              }}
              sx={{
                mb: 2.5,
                bgcolor: "#ececf0",
                borderRadius: "999px",
                p: "6px",
                gap: "4px",
                "& .MuiToggleButton-root": {
                  border: 0,
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "999px !important",
                  color: CC.textPrimary,
                  fontFamily: CC.font,
                  "&:hover": { bgcolor: "#000", color: "#fff" },
                  "&.Mui-selected": { bgcolor: "#000", color: "#fff", fontWeight: 600, "&:hover": { bgcolor: "#000" } },
                },
              }}
            >
              <ToggleButton value="product">Promote a Product</ToggleButton>
              <ToggleButton value="site">Promote the Whole Site</ToggleButton>
            </ToggleButtonGroup>
          )}
          {hasPickableProducts && promoMode === "product" && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
                    Select a product to promote
                  </Typography>
                  <Typography variant="caption" sx={{ color: CC.textSecondary, fontFamily: CC.font }}>
                    Pick a product to generate a link for it specifically — {linkedStorefront?.store_name || "the brand"}'s picks plus anything you've added yourself.
                  </Typography>
                </Box>
                <Chip
                  label={`${storefrontProducts.length} product${storefrontProducts.length === 1 ? "" : "s"}`}
                  size="small"
                  sx={{ height: 22, fontSize: 11, fontWeight: 600, fontFamily: CC.font, borderRadius: "999px", bgcolor: CC.bg, color: CC.textSecondary, flexShrink: 0 }}
                />
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 1.5 }}>
                  {storefrontProducts.map((p) => {
                    const selected = selectedProductId === p.product_id && selectedProductSource === p.__source;
                    const hasDiscount =
                      p.discount_price !== null && p.discount_price !== undefined && Number(p.discount_price) < Number(p.price);
                    return (
                      <Box
                        key={`${p.__source}-${p.product_id}`}
                        onClick={() => selectProduct(p)}
                        role="button"
                        tabIndex={0}
                        sx={{
                          borderRadius: CC.radiusMd,
                          border: `1.5px solid ${selected ? CC.primary : CC.border}`,
                          bgcolor: "#fff",
                          overflow: "hidden",
                          cursor: "pointer",
                          transition: "border-color .15s ease, box-shadow .15s ease",
                          boxShadow: selected ? `0 0 0 3px ${CC.primary}1F` : "none",
                          "&:hover": { borderColor: selected ? CC.primary : CC.borderHover },
                        }}
                      >
                        <Box sx={{ position: "relative", width: "100%", pt: "75%", bgcolor: "#F5F5F5" }}>
                          {p.primary_image ? (
                            <Box
                              component="img"
                              src={p.primary_image}
                              alt={p.product_title}
                              sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <StorefrontOutlinedIcon sx={{ fontSize: 28, color: "#c7cad1" }} />
                            </Box>
                          )}
                          {selected && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 6,
                                right: 6,
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                bgcolor: CC.primary,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                              }}
                            >
                              <CheckRoundedIcon sx={{ fontSize: 15, color: "#fff" }} />
                            </Box>
                          )}
                          {hasDiscount && p.discount_percent ? (
                            <Chip
                              label={`${Math.round(p.discount_percent)}% off`}
                              size="small"
                              sx={{ position: "absolute", bottom: 6, left: 6, height: 20, fontSize: 10, fontWeight: 700, borderRadius: "999px", bgcolor: CC.accent, color: "#fff" }}
                            />
                          ) : null}
                          {p.__source === "mine" && (
                            <Chip
                              label="Added by you"
                              size="small"
                              sx={{ position: "absolute", bottom: 6, right: 6, height: 20, fontSize: 10, fontWeight: 700, borderRadius: "999px", bgcolor: "#fff", color: CC.textPrimary, border: `1px solid ${CC.border}` }}
                            />
                          )}
                        </Box>
                        <Box sx={{ p: 1.25 }}>
                          {p.category && (
                            <Typography
                              variant="caption"
                              sx={{ color: CC.textSecondary, fontFamily: CC.font, textTransform: "uppercase", letterSpacing: 0.3, fontSize: 10, fontWeight: 700 }}
                            >
                              {p.category}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            title={p.product_title}
                            sx={{
                              color: CC.textPrimary,
                              fontFamily: CC.font,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: 1.3,
                              mt: 0.25,
                            }}
                          >
                            {p.product_title}
                          </Typography>
                          {p.product_description && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: CC.textSecondary,
                                fontFamily: CC.font,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                mt: 0.5,
                              }}
                            >
                              {p.product_description}
                            </Typography>
                          )}
                          {(p.price !== null && p.price !== undefined) && (
                            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mt: 0.75 }}>
                              {hasDiscount ? (
                                <>
                                  <Typography variant="body2" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
                                    {sym(p.currency)}
                                    {Number(p.discount_price).toFixed(0)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: CC.textSecondary, fontFamily: CC.font, textDecoration: "line-through" }}>
                                    {sym(p.currency)}
                                    {Number(p.price).toFixed(0)}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
                                  {sym(p.currency)}
                                  {Number(p.price).toFixed(0)}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 520 }}>
            {(!hasPickableProducts || promoMode === "site") && (
              <>
                {landingPages.length > 0 && (
                  <FormControl size="small" fullWidth sx={fieldSx}>
                    <InputLabel id="cashkaro-landing-page-label">Landing page</InputLabel>
                    <Select
                      labelId="cashkaro-landing-page-label"
                      label="Landing page"
                      value={landingPages.some((lp) => lp.tracking_link === targetUrl) ? targetUrl : ""}
                      onChange={(e) => {
                        setTargetUrl(String(e.target.value));
                        setSelectedProductId(null);
                        setSelectedProductSource(null);
                      }}
                      sx={{ borderRadius: CC.radiusSm, fontFamily: CC.font }}
                    >
                      {landingPages.map((lp) => (
                        <MenuItem key={lp.id ?? lp.tracking_link} value={lp.tracking_link || ""}>
                          {lp.title || lp.tracking_link}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  label="Destination URL"
                  placeholder="https://brand.com/product-page"
                  value={targetUrl}
                  onChange={(e) => {
                    setTargetUrl(e.target.value);
                    // Hand-editing the URL after picking a product breaks the
                    // association — don't send a storefront_product_id/campaign_product_id
                    // that no longer matches what target_url actually points at.
                    if (selectedProductId !== null) {
                      setSelectedProductId(null);
                      setSelectedProductSource(null);
                    }
                  }}
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkOutlinedIcon fontSize="small" sx={{ color: CC.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
            {hasPickableProducts && promoMode === "product" && (
              <Alert severity="info" icon={false} sx={{ borderRadius: CC.radiusMd, fontFamily: CC.font, bgcolor: CC.bg, color: CC.textPrimary, py: 0.5 }}>
                {selectedProductId ? "Link will point to the selected product above." : "Pick a product above to generate a link for it."}
              </Alert>
            )}
            <TextField
              label="Label (optional)"
              placeholder="e.g. Instagram story"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              size="small"
              fullWidth
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LabelOutlinedIcon fontSize="small" sx={{ color: CC.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                disableElevation
                onClick={handleGenerateLink}
                disabled={generating || !targetUrl.trim()}
                startIcon={!generating ? <LinkOutlinedIcon fontSize="small" /> : undefined}
                sx={{
                  bgcolor: "#000",
                  fontFamily: CC.font,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "999px",
                  px: 2.5,
                  "&:hover": { bgcolor: "#111" },
                  "&.Mui-disabled": { bgcolor: "#000", color: "#fff", opacity: 0.7 },
                }}
              >
                {generating ? "Generating…" : "Generate Link"}
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ borderRadius: CC.radiusMd, fontFamily: CC.font }}>
                {error}
              </Alert>
            )}

            {generatedLink && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.75,
                  bgcolor: CC.primaryWash,
                  borderLeft: `3px solid ${CC.primary}`,
                  borderRadius: CC.radiusSm,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
                  <CheckCircleRoundedIcon sx={{ fontSize: 15, color: CC.primary }} />
                  <Typography variant="caption" fontWeight={700} sx={{ color: CC.primaryDark, fontFamily: CC.font }}>
                    Your trackable link is ready
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: "break-all", color: CC.textPrimary, fontFamily: CC.font }}>
                    {generatedLink}
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleCopy}
                    startIcon={copied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    sx={{
                      flexShrink: 0,
                      textTransform: "none",
                      fontWeight: 600,
                      fontFamily: CC.font,
                      borderRadius: "999px",
                      px: 1.5,
                      bgcolor: "#fff",
                      color: copied ? "success.main" : CC.primary,
                      border: `1px solid ${copied ? "success.main" : CC.primary}`,
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </Box>
              </Paper>
            )}
            {generatedLink && (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                  <RocketLaunchOutlinedIcon fontSize="small" sx={{ color: CC.primary }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
                    Promote it
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    onClick={handleAddToStorefront}
                    className="btn btn-outline-primary btn-rounded btn-sm"
                    disabled={addingStorefront || addedStorefront}
                    startIcon={
                      addingStorefront ? (
                        <CircularProgress size={14} />
                      ) : addedStorefront ? (
                        <CheckRoundedIcon fontSize="small" />
                      ) : (
                        <StorefrontOutlinedIcon fontSize="small" />
                      )
                    }
                    
                  >
                    {addedStorefront ? "Added to Storefront" : "Add to Storefront"}
                  </Button>
                  <Button
                    size="small"
                    onClick={handleAddToBio}
                    disabled={addingBio || addedBio}
                    className="btn btn-outline-primary btn-rounded btn-sm"
                    startIcon={
                      addingBio ? (
                        <CircularProgress size={14} />
                      ) : addedBio ? (
                        <CheckRoundedIcon fontSize="small" />
                      ) : (
                        <PersonPinCircleOutlinedIcon fontSize="small" />
                      )
                    }
                    
                  >
                    {addedBio ? "Added to Bio" : "Add to Link in Bio"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      </Box>
      </Box>

      <AddCampaignProductDialog
        open={addProductDialogOpen}
        onClose={() => setAddProductDialogOpen(false)}
        campaignId={Number(id)}
        influencerId={influencerId}
        onAdded={handleProductAdded}
      />

      <Snackbar
        open={!!crossPostSnackbar}
        autoHideDuration={4000}
        onClose={() => setCrossPostSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {crossPostSnackbar ? (
          <Alert
            severity={crossPostSnackbar.severity}
            onClose={() => setCrossPostSnackbar(null)}
            action={
              crossPostSnackbar.actionTo ? (
                <Button color="inherit" size="small" onClick={() => navigate(crossPostSnackbar.actionTo!)} sx={{ textTransform: "none", fontWeight: 600 }}>
                  Set up
                </Button>
              ) : undefined
            }
          >
            {crossPostSnackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default CashkaroCampaignDetail;
